import type { SettingsPage } from "../types";
import { getSettingValue } from "../settingsStore";
import { sync } from "../../cloud/sync";
import { qdaSync } from "../../cloud/qdaSync";

const backendSettings: SettingsPage = {
  id: "backend",
  label: "Backend",
  order: 2,
  items: [
    {
      key: "pluginHeading",
      label: "Plugin",
      type: "heading",
      default: "",
    },
    {
      key: "yaqatwUrl",
      label: "Yaqat",
      type: "text",
      default: "https://yaqatw.alefa.net/embed.html",
      description:
        "URL to load the yaqatw plugin from. Applies the next time the app starts.",
    },
    {
      key: "cloudProviderHeading",
      label: "Cloud provider",
      type: "heading",
      default: "",
    },
    {
      key: "cloudServer",
      label: "Cloud server",
      type: "select",
      default: "",
      options: [
        { value: "", label: "Disabled" },
        { value: "googleDrive", label: "Google Drive" },
        { value: "dropBox", label: "Dropbox" },
        { value: "oneDrive", label: "OneDrive" },
        { value: "nextCloud", label: "Nextcloud" },
        { value: "s3", label: "S3-compatible storage" },
      ],
      description:
        "Cloud service to sync project data across devices. Choose Disabled to keep everything local only.",
    },
    {
      key: "nextcloudUrl",
      label: "Nextcloud: Server URL",
      type: "text",
      default: "",
      description: "Your Nextcloud instance, e.g. https://cloud.example.com.",
      visibleWhen: { key: "cloudServer", equals: "nextCloud" },
    },
    {
      key: "nextcloudOAuthClientId",
      label: "Nextcloud: OAuth client ID",
      type: "password",
      default: "",
      description:
        "Create an OAuth app in Nextcloud Settings > Security > OAuth 2.0 clients.",
      visibleWhen: { key: "cloudServer", equals: "nextCloud" },
    },
    {
      key: "nextcloudOAuthClientSecret",
      label: "Nextcloud: OAuth client secret",
      type: "password",
      default: "",
      description: "The client secret from the same Nextcloud OAuth app.",
      visibleWhen: { key: "cloudServer", equals: "nextCloud" },
    },
    {
      key: "nextcloudUserName",
      label: "Nextcloud: Username",
      type: "text",
      default: "",
      description: "Auto-filled after your first successful sync; you can also set it manually.",
      visibleWhen: { key: "cloudServer", equals: "nextCloud" },
    },
    {
      key: "s3Endpoint",
      label: "S3: Endpoint",
      type: "text",
      default: "",
      description: "Scheme + host, e.g. https://s3.amazonaws.com (no trailing slash).",
      visibleWhen: { key: "cloudServer", equals: "s3" },
    },
    {
      key: "s3Region",
      label: "S3: Region",
      type: "text",
      default: "us-east-1",
      visibleWhen: { key: "cloudServer", equals: "s3" },
    },
    {
      key: "s3BucketName",
      label: "S3: Bucket name",
      type: "text",
      default: "",
      visibleWhen: { key: "cloudServer", equals: "s3" },
    },
    {
      key: "s3AccessKeyId",
      label: "S3: Access key ID",
      type: "password",
      default: "",
      visibleWhen: { key: "cloudServer", equals: "s3" },
    },
    {
      key: "s3SecretAccessKey",
      label: "S3: Secret access key",
      type: "password",
      default: "",
      visibleWhen: { key: "cloudServer", equals: "s3" },
    },
    {
      key: "syncHeading",
      label: "Sync",
      type: "heading",
      default: "",
    },
    {
      key: "syncIntervalMinutes",
      label: "Sync interval (minutes)",
      type: "number",
      default: 0,
      min: 0,
      max: 1440,
      step: 5,
      description: "Automatically syncs on this interval while a project is open. 0 disables automatic sync.",
    },
    {
      key: "syncNow",
      label: "Sync now",
      type: "action",
      default: "",
      description: "Manually sync the current project's documents and highlights with the selected cloud server.",
      onClick: () => qdaSync.syncNow({ resetAuthGate: true }),
    },
    {
      key: "pullCloudVersion",
      label: "Pull cloud version (cloud wins)",
      type: "action",
      default: "",
      description:
        "Overwrites this device's copy of the current project's documents and highlights with the cloud copy, ignoring modified times. Use if this device is stuck showing an older version than the cloud. Only pulls — it never uploads — but any unsynced local edits to these documents are lost.",
      onClick: () => qdaSync.pullFromCloud(),
    },
    {
      key: "clearCloudCredentials",
      label: "Clear cloud credentials",
      type: "action",
      default: "",
      description: "Signs out of the currently selected cloud server; you'll be prompted to reconnect on the next sync.",
      onClick: async () => {
        const backend = getSettingValue("backend", "cloudServer");
        if (!backend || typeof backend !== "string") {
          throw new Error("No cloud server selected.");
        }
        await sync.clearToken(backend);
      },
    },
    {
      key: "advancedHeading",
      label: "Advanced",
      type: "heading",
      default: "",
    },
    {
      key: "cloudTimeoutSec",
      label: "Cloud request timeout (seconds)",
      type: "number",
      default: 15,
      min: 5,
      max: 120,
      description: "How long to wait for a cloud request before it's considered failed.",
    },
    {
      key: "rateLimitRetryAttempts",
      label: "Rate-limit retry attempts",
      type: "number",
      default: 5,
      min: 0,
      max: 10,
      description: "How many times to retry a request after a rate-limit (429) response, with backoff.",
    },
    {
      key: "syncDebugLogging",
      label: "Sync debug logging",
      type: "toggle",
      default: false,
      description: "Logs detailed per-file sync activity to the browser console.",
    },
  ],
};

export default backendSettings;
